import { ref, Ref } from 'vue';
import { instanceToInstance } from 'class-transformer';
import type { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useInspectionDeviceStore } from '@/store/entities/inspectionDeviceStore';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { useRoomStore } from '@/store/entities/roomStore';
import { useRoomGroupStore } from '@/store/entities/roomGroupStore';
import { LoRaWanRadioCheck } from '@/api/funkcheck/LoRaWanRadioCheck';
import {
  DeviceRadioCheckStatus,
  OverallRadioCheckStatus,
} from '@/enums/radiocheck';
import { useRadioCheckStatus } from '@/composables/useRadioCheckStatus';

export type DeviceInfo = {
  deviceId: string;
  status: DeviceRadioCheckStatus;
  floor?: string;
  number?: number;
  doorbellName?: string;
  statusIcon?: string;
  statusType?: string;
};

type GroupedDevicesByFloor = { floor: string; entries: DeviceInfo[] }[];

export type InspectionPointsLocation = {
  inspectionPoint: InspectionPoint;
  floor?: string;
  roomGroupLabel?: string;
  hasResult?: boolean;
  statusIcon?: string;
  statusType?: string;
  statusLabel?: string;
  groupedDevices?: GroupedDevicesByFloor;
};

export const useInstallationLocationsTable = () => {
  const radioCheckResult = ref<LoRaWanRadioCheck>();

  const inspectionPointStore = useInspectionPointStore();

  const buildInspectionPointsArray = (
    inspectionPoints: Array<InspectionPoint>
  ) => {
    const result: Array<InspectionPointsLocation> = [];
    inspectionPoints.forEach((inspectionPoint: InspectionPoint) => {
      const radioCheckresult = inspectionPoint.inspectionData.radioCheckResult;
      result.push({
        inspectionPoint,
        floor: getFloor(inspectionPoint),
        roomGroupLabel: getRoomGroupLabel(inspectionPoint),
        hasResult: hasPossibleInstallationLocations(inspectionPoint),
        statusIcon: getStatusIcon(
          radioCheckresult ? radioCheckresult : OverallRadioCheckStatus.CANCELED
        ),
        statusType: getStatusColor(
          radioCheckresult ? radioCheckresult : OverallRadioCheckStatus.CANCELED
        ),
        statusLabel: getStatusLabel(
          radioCheckresult ? radioCheckresult : OverallRadioCheckStatus.CANCELED
        ),
        groupedDevices: groupedByFloor(inspectionPoint),
      });
    });
    return result;
  };

  const buildDeviceInfo = (inspectionPoint: InspectionPoint) => {
    const srtInspectionPoints =
      inspectionPointStore.getSrtInspectionPointsForCurrentOrder();
    const roomStore = useRoomStore();
    const roomGroupStore = useRoomGroupStore();

    radioCheckResult.value =
      inspectionPointStore.getLatestRadioCheckForInspectionPoint(
        inspectionPoint
      );

    const deviceInfo = [];
    for (const inspectionPoint of srtInspectionPoints) {
      const result = radioCheckResult.value?.result?.[inspectionPoint.id];
      if (!result) continue;
      const room = inspectionPoint?.roomId
        ? roomStore.getRoomById(inspectionPoint.roomId)
        : undefined;
      const floor = room?.getFloor?.() ?? undefined;

      const roomGroup = room?.roomGroupId
        ? roomGroupStore.getRoomGroupById(room?.roomGroupId)
        : undefined;
      const doorbellName = roomGroup?.getLabel?.() ?? 'Unbekannt';
      const number = roomGroup?.number ?? undefined;
      const statusIcon = getStatusIcon(result.status);
      const statusType = getStatusType(result.status);

      deviceInfo.push({
        deviceId: inspectionPoint.id,
        status: result.status,
        floor,
        number,
        doorbellName,
        statusIcon,
        statusType,
      });
    }
    return deviceInfo;
  };

  const getStatusIcon = (
    status: DeviceRadioCheckStatus | OverallRadioCheckStatus
  ) => {
    const { statusIcon } = useRadioCheckStatus(ref(status));
    return statusIcon.value;
  };

  const getStatusType = (status: DeviceRadioCheckStatus) => {
    const { statusType } = useRadioCheckStatus(ref(status));
    return statusType.value.toLowerCase();
  };

  const getStatusLabel = (status: OverallRadioCheckStatus) => {
    const { statusGwLabel } = useRadioCheckStatus(ref(status));
    return statusGwLabel.value;
  };

  const getStatusColor = (status: OverallRadioCheckStatus) => {
    const { statusGwColor } = useRadioCheckStatus(ref(status));
    return statusGwColor.value;
  };

  const getFloor = (inspectionPoint: InspectionPoint) => {
    if (!inspectionPoint) return '—';
    return useRoomStore().getRoomById(inspectionPoint.roomId).getFloor();
  };

  const getRoomGroupLabel = (inspectionPoint: InspectionPoint) => {
    if (!inspectionPoint) return '—';
    const room = useRoomStore().getRoomById(inspectionPoint.roomId);
    const roomGroupId = room.roomGroupId;
    if (!roomGroupId) return '—';
    return useRoomGroupStore().getRoomGroupById(roomGroupId).getLabel();
  };

  const hasPossibleInstallationLocations = (inspectionPoint: InspectionPoint) =>
    inspectionPoint.inspectionData.radioCheckResult ===
      OverallRadioCheckStatus.INCOMPLETE ||
    inspectionPoint.inspectionData.radioCheckResult ===
      OverallRadioCheckStatus.COMPLETED;

  const groupedByFloor = (
    inspectionPoint: InspectionPoint
  ): GroupedDevicesByFloor => {
    const deviceInfo = buildDeviceInfo(inspectionPoint);
    const map = new Map<string, DeviceInfo[]>();
    for (const item of deviceInfo) {
      const key = item.floor ?? '-';
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(item);
    }
    return Array.from(map, ([floor, entries]) => ({ floor, entries }));
  };

  return {
    groupedByFloor,
    hasPossibleInstallationLocations,
    buildDeviceInfo,
    getFloor,
    getRoomGroupLabel,
    buildInspectionPointsArray,
  };
};
